import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Users, Search, GraduationCap, BookOpen, Plus, Building2, MapPin, DollarSign, Briefcase, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmployerDashboard() {
  const { user } = useAuth();
  const [graduates, setGraduates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPostForm, setShowPostForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobForm, setJobForm] = useState({
    title: '', company: '', location: '', salary_range: '',
    description: '', requirements: '', job_type: 'full-time'
  });
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/graduates`, { withCredentials: true })
      .then(r => setGraduates(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const postJob = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      const reqs = jobForm.requirements.split(',').map(s => s.trim()).filter(Boolean);
      await axios.post(`${API}/jobs/post`, { ...jobForm, requirements: reqs }, { withCredentials: true });
      alert('Job posted successfully!');
      setShowPostForm(false);
      setJobForm({ title: '', company: '', location: '', salary_range: '', description: '', requirements: '', job_type: 'full-time' });
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to post job');
    }
    setPosting(false);
  };

  const filteredGrads = graduates.filter(g =>
    !searchTerm || g.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.profile?.skills?.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div data-testid="employer-dashboard" className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex justify-between items-start mb-12">
          <div>
            <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Employer Dashboard</p>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Welcome, {user?.name || 'Employer'}
            </h1>
          </div>
          <button
            onClick={() => setShowPostForm(!showPostForm)}
            data-testid="post-job-toggle"
            className="brand-btn text-sm px-6 py-3 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} /> Post a Job
          </button>
        </div>

        {/* Post Job Form */}
        {showPostForm && (
          <div data-testid="post-job-form" className="border border-[#262626] p-8 mb-12">
            <h2 className="text-xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Post a New Job</h2>
            <form onSubmit={postJob} className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-200 block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Job Title</label>
                <input data-testid="job-title-input" value={jobForm.title} onChange={e => setJobForm({...jobForm, title: e.target.value})} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-base text-white placeholder-zinc-500" placeholder="e.g., ML Engineer" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-200 block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Company</label>
                <input data-testid="job-company-input" value={jobForm.company} onChange={e => setJobForm({...jobForm, company: e.target.value})} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-base text-white placeholder-zinc-500" placeholder="Company name" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-200 block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Location</label>
                <input data-testid="job-location-input" value={jobForm.location} onChange={e => setJobForm({...jobForm, location: e.target.value})} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-base text-white placeholder-zinc-500" placeholder="e.g., Remote / New York" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-200 block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Salary Range</label>
                <input data-testid="job-salary-input" value={jobForm.salary_range} onChange={e => setJobForm({...jobForm, salary_range: e.target.value})} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-base text-white placeholder-zinc-500" placeholder="e.g., $80K - $120K" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-200 block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Description</label>
                <textarea data-testid="job-description-input" value={jobForm.description} onChange={e => setJobForm({...jobForm, description: e.target.value})} required rows={3} className="w-full px-4 py-3 border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-sm resize-none" placeholder="Job description..." />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-200 block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Requirements (comma-separated)</label>
                <input data-testid="job-requirements-input" value={jobForm.requirements} onChange={e => setJobForm({...jobForm, requirements: e.target.value})} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-base text-white placeholder-zinc-500" placeholder="e.g., Python, TensorFlow, 2+ years experience" />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button type="submit" disabled={posting} data-testid="submit-job-post" className="brand-btn text-sm px-8 py-3 disabled:opacity-50">
                  {posting ? 'Posting...' : 'Post Job'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Graduate Directory */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Graduate Directory</h2>
            <div className="flex items-center border border-[#262626] px-3 w-64">
              <Search className="w-4 h-4 text-zinc-200" strokeWidth={1.5} />
              <input
                data-testid="graduate-search"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search by name or skill..."
                className="flex-1 px-3 py-2 text-base bg-transparent text-white placeholder-zinc-500 outline-none"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-16 text-zinc-200">Loading graduates...</div>
          ) : filteredGrads.length === 0 ? (
            <div className="text-center py-16 border border-[#262626]">
              <GraduationCap className="w-16 h-16 text-zinc-300 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-zinc-300">No graduates found</p>
              <p className="text-sm text-zinc-200 mt-1">Graduates will appear here once students complete courses and enable their profile.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#262626]">
              {filteredGrads.map((grad, i) => (
                <div key={i} data-testid={`graduate-card-${i}`} className="p-6 border-b border-r border-[#262626]">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#0077B6]/10 flex items-center justify-center">
                      <GraduationCap className="w-10 h-10 text-[#0077B6]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{grad.name}</p>
                      <p className="text-xs text-zinc-200">{grad.email}</p>
                    </div>
                  </div>
                  {grad.profile?.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {grad.profile.skills.map((s, j) => (
                        <span key={j} className="text-sm font-mono px-2 py-1 bg-zinc-100 text-zinc-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s}</span>
                      ))}
                    </div>
                  )}
                  {grad.enrollments?.length > 0 && (
                    <p className="text-xs text-zinc-200">{grad.enrollments.length} course(s) completed</p>
                  )}
                  {grad.profile?.bio && <p className="text-sm text-zinc-300 mt-2 line-clamp-2">{grad.profile.bio}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
